'use strict';

Object.defineProperty(exports, '__esModule', {
  value: true
});
function _interopDefault(ex) {
  return ex && typeof ex === 'object' && 'default' in ex ? ex['default'] : ex;
}
var React = _interopDefault(require('react'));
var styled = _interopDefault(require('styled-components'));
var functions = require('@keytrade/functions');
var componentsButton = require('@keytrade/components-button');
var commonjsGlobal = typeof window !== 'undefined' ? window : typeof global !== 'undefined' ? global : typeof self !== 'undefined' ? self : {};
function createCommonjsModule(fn, module) {
  return module = {
    exports: {}
  }, fn(module, module.exports), module.exports;
}
var focusVisible = createCommonjsModule(function (module, exports) {
  (function (global, factory) {
    factory();
  })(commonjsGlobal, function () {
    /**
     * Applies the :focus-visible polyfill at the given scope.
     * A scope in this case is either the top-level Document or a Shadow Root.
     *
     * @param {(Document|ShadowRoot)} scope
     * @see https://github.com/WICG/focus-visible
     */
    function applyFocusVisiblePolyfill(scope) {
      var hadKeyboardEvent = true;
      var hadFocusVisibleRecently = false;
      var hadFocusVisibleRecentlyTimeout = null;
      var inputTypesAllowlist = {
        text: true,
        search: true,
        url: true,
        tel: true,
        email: true,
        password: true,
        number: true,
        date: true,
        month: true,
        week: true,
        time: true,
        datetime: true,
        'datetime-local': true
      };

      /**
       * Helper function for legacy browsers and iframes which sometimes focus
       * elements like document, body, and non-interactive SVG.
       * @param {Element} el
       */
      function isValidFocusTarget(el) {
        if (el && el !== document && el.nodeName !== 'HTML' && el.nodeName !== 'BODY' && 'classList' in el && 'contains' in el.classList) {
          return true;
        }
        return false;
      }

      /**
       * Computes whether the given element should automatically trigger the
       * `focus-visible` class being added, i.e. whether it should always match
       * `:focus-visible` when focused.
       * @param {Element} el
       * @return {boolean}
       */
      function focusTriggersKeyboardModality(el) {
        var type = el.type;
        var tagName = el.tagName;
        if (tagName === 'INPUT' && inputTypesAllowlist[type] && !el.readOnly) {
          return true;
        }
        if (tagName === 'TEXTAREA' && !el.readOnly) {
          return true;
        }
        if (el.isContentEditable) {
          return true;
        }
        return false;
      }

      /**
       * Add the `focus-visible` class to the given element if it was not added by
       * the author.
       * @param {Element} el
       */
      function addFocusVisibleClass(el) {
        if (el.classList.contains('focus-visible')) {
          return;
        }
        el.classList.add('focus-visible');
        el.setAttribute('data-focus-visible-added', '');
      }

      /**
       * Remove the `focus-visible` class from the given element if it was not
       * originally added by the author.
       * @param {Element} el
       */
      function removeFocusVisibleClass(el) {
        if (!el.hasAttribute('data-focus-visible-added')) {
          return;
        }
        el.classList.remove('focus-visible');
        el.removeAttribute('data-focus-visible-added');
      }

      /**
       * If the most recent user interaction was via the keyboard;
       * and the key press did not include a meta, alt/option, or control key;
       * then the modality is keyboard. Otherwise, the modality is not keyboard.
       * Apply `focus-visible` to any current active element and keep track
       * of our keyboard modality state with `hadKeyboardEvent`.
       * @param {KeyboardEvent} e
       */
      function onKeyDown(e) {
        if (e.metaKey || e.altKey || e.ctrlKey) {
          return;
        }
        if (isValidFocusTarget(scope.activeElement)) {
          addFocusVisibleClass(scope.activeElement);
        }
        hadKeyboardEvent = true;
      }

      /**
       * If at any point a user clicks with a pointing device, ensure that we change
       * the modality away from keyboard.
       * This avoids the situation where a user presses a key on an already focused
       * element, and then clicks on a different element, focusing it with a
       * pointing device, while we still think we're in keyboard modality.
       * @param {Event} e
       */
      function onPointerDown(e) {
        hadKeyboardEvent = false;
      }

      /**
       * On `focus`, add the `focus-visible` class to the target if:
       * - the target received focus as a result of keyboard navigation, or
       * - the event target is an element that will likely require interaction
       *   via the keyboard (e.g. a text box)
       * @param {Event} e
       */
      function onFocus(e) {
        // Prevent IE from focusing the document or HTML element.
        if (!isValidFocusTarget(e.target)) {
          return;
        }
        if (hadKeyboardEvent || focusTriggersKeyboardModality(e.target)) {
          addFocusVisibleClass(e.target);
        }
      }

      /**
       * On `blur`, remove the `focus-visible` class from the target.
       * @param {Event} e
       */
      function onBlur(e) {
        if (!isValidFocusTarget(e.target)) {
          return;
        }
        if (e.target.classList.contains('focus-visible') || e.target.hasAttribute('data-focus-visible-added')) {
          // To detect a tab/window switch, we look for a blur event followed
          // rapidly by a visibility change.
          // If we don't see a visibility change within 100ms, it's probably a
          // regular focus change.
          hadFocusVisibleRecently = true;
          window.clearTimeout(hadFocusVisibleRecentlyTimeout);
          hadFocusVisibleRecentlyTimeout = window.setTimeout(function () {
            hadFocusVisibleRecently = false;
          }, 100);
          removeFocusVisibleClass(e.target);
        }
      }

      /**
       * If the user changes tabs, keep track of whether or not the previously
       * focused element had .focus-visible.
       * @param {Event} e
       */
      function onVisibilityChange(e) {
        if (document.visibilityState === 'hidden') {
          // If the tab becomes active again, the browser will handle calling focus
          // on the element (Safari actually calls it twice).
          // If this tab change caused a blur on an element with focus-visible,
          // re-apply the class when the user switches back to the tab.
          if (hadFocusVisibleRecently) {
            hadKeyboardEvent = true;
          }
          addInitialPointerMoveListeners();
        }
      }

      /**
       * Add a group of listeners to detect usage of any pointing devices.
       * These listeners will be added when the polyfill first loads, and anytime
       * the window is blurred, so that they are active when the window regains
       * focus.
       */
      function addInitialPointerMoveListeners() {
        document.addEventListener('mousemove', onInitialPointerMove);
        document.addEventListener('mousedown', onInitialPointerMove);
        document.addEventListener('mouseup', onInitialPointerMove);
        document.addEventListener('pointermove', onInitialPointerMove);
        document.addEventListener('pointerdown', onInitialPointerMove);
        document.addEventListener('pointerup', onInitialPointerMove);
        document.addEventListener('touchmove', onInitialPointerMove);
        document.addEventListener('touchstart', onInitialPointerMove);
        document.addEventListener('touchend', onInitialPointerMove);
      }
      function removeInitialPointerMoveListeners() {
        document.removeEventListener('mousemove', onInitialPointerMove);
        document.removeEventListener('mousedown', onInitialPointerMove);
        document.removeEventListener('mouseup', onInitialPointerMove);
        document.removeEventListener('pointermove', onInitialPointerMove);
        document.removeEventListener('pointerdown', onInitialPointerMove);
        document.removeEventListener('pointerup', onInitialPointerMove);
        document.removeEventListener('touchmove', onInitialPointerMove);
        document.removeEventListener('touchstart', onInitialPointerMove);
        document.removeEventListener('touchend', onInitialPointerMove);
      }

      /**
       * When the polfyill first loads, assume the user is in keyboard modality.
       * If any event is received from a pointing device (e.g. mouse, pointer,
       * touch), turn off keyboard modality.
       * This accounts for situations where focus enters the page from the URL bar.
       * @param {Event} e
       */
      function onInitialPointerMove(e) {
        // Work around a Safari quirk that fires a mousemove on <html> whenever the
        // window blurs, even if you're tabbing out of the page. ¯\_(ツ)_/¯
        if (e.target.nodeName && e.target.nodeName.toLowerCase() === 'html') {
          return;
        }
        hadKeyboardEvent = false;
        removeInitialPointerMoveListeners();
      }

      // For some kinds of state, we are interested in changes at the global scope
      // only. For example, global pointer input, global key presses and global
      // visibility change should affect the state at every scope:
      document.addEventListener('keydown', onKeyDown, true);
      document.addEventListener('mousedown', onPointerDown, true);
      document.addEventListener('pointerdown', onPointerDown, true);
      document.addEventListener('touchstart', onPointerDown, true);
      document.addEventListener('visibilitychange', onVisibilityChange, true);
      addInitialPointerMoveListeners();

      // For focus and blur, we specifically care about state changes in the local
      // scope. This is because focus / blur events that originate from within a
      // shadow root are not re-dispatched from the host element if it was already
      // the active element in its own scope:
      scope.addEventListener('focus', onFocus, true);
      scope.addEventListener('blur', onBlur, true);

      // We detect that a node is a ShadowRoot by ensuring that it is a
      // DocumentFragment and also has a host property. This check covers native
      // implementation and polyfill implementation transparently. If we only cared
      // about the native implementation, we could just check if the scope was
      // an instance of a ShadowRoot.
      if (scope.nodeType === Node.DOCUMENT_FRAGMENT_NODE && scope.host) {
        // Since a ShadowRoot is a special kind of DocumentFragment, it does not
        // have a root element to add a class to. So, we add this attribute to the
        // host element instead:
        scope.host.setAttribute('data-js-focus-visible', '');
      } else if (scope.nodeType === Node.DOCUMENT_NODE) {
        document.documentElement.classList.add('js-focus-visible');
        document.documentElement.setAttribute('data-js-focus-visible', '');
      }
    }

    // It is important to wrap all references to global window and document in
    // these checks to support server-side rendering use cases
    // @see https://github.com/WICG/focus-visible/issues/199
    if (typeof window !== 'undefined' && typeof document !== 'undefined') {
      // Make the polyfill helper globally available. This can be used as a signal
      // to interested libraries that wish to coordinate with the polyfill for e.g.,
      // applying the polyfill to a shadow root:
      window.applyFocusVisiblePolyfill = applyFocusVisiblePolyfill;

      // Notify interested libraries of the polyfill's presence, in case the
      // polyfill was loaded lazily:
      var event;
      try {
        event = new CustomEvent('focus-visible-polyfill-ready');
      } catch (error) {
        // IE11 does not support using CustomEvent as a constructor directly:
        event = document.createEvent('CustomEvent');
        event.initCustomEvent('focus-visible-polyfill-ready', false, false, {});
      }
      window.dispatchEvent(event);
    }
    if (typeof document !== 'undefined') {
      // Apply the polyfill to the global document, so that no JavaScript
      // coordination is required to use the polyfill in the top-level document:
      applyFocusVisiblePolyfill(document);
    }
  });
});
var ArrowLeft = function ArrowLeft(props) {
  return /*#__PURE__*/React.createElement("svg", {
    role: "img",
    xmlns: "http://www.w3.org/2000/svg",
    width: "2.4rem",
    height: "2.4rem",
    viewBox: "0 0 24 24",
    className: props.className
  }, /*#__PURE__*/React.createElement("title", null, "Arrow Left"), /*#__PURE__*/React.createElement("path", {
    fill: props.color,
    fillRule: "evenodd",
    d: "M8.177 11.88l5.89 5.844a.597.597 0 0 0 .85 0l.355-.358a.609.609 0 0 0 0-.856l-5.115-5.06 5.11-5.058a.609.609 0 0 0 0-.857l-.355-.357a.597.597 0 0 0-.85 0l-5.89 5.845c-.23.236-.23.62.005.856z"
  }));
};
var ArrowRight = function ArrowRight(props) {
  return /*#__PURE__*/React.createElement("svg", {
    role: "img",
    xmlns: "http://www.w3.org/2000/svg",
    width: "2.4rem",
    height: "2.4rem",
    viewBox: "0 0 24 24",
    className: props.className
  }, /*#__PURE__*/React.createElement("title", null, "Arrow Right"), /*#__PURE__*/React.createElement("path", {
    fill: props.color,
    fillRule: "evenodd",
    d: "M16.271 11.88l-5.89 5.844a.597.597 0 0 1-.85 0l-.355-.358a.609.609 0 0 1 0-.856l5.115-5.06-5.11-5.058a.609.609 0 0 1 0-.857l.355-.357a.597.597 0 0 1 .85 0l5.89 5.845c.23.236.23.62-.005.856z"
  }));
};
var colors = functions.config.colors.list;
var getFont = functions.config.fonts.getFont;
var Wrapper = styled.div.withConfig({
  displayName: "Pagination__Wrapper",
  componentId: "sc-zcpmm5-0"
})(["display:inline-flex;padding:0.4rem;background-color:", ";border-radius:5px;", ";margin:", ";"], function (props) {
  return props.transparentBackground ? 'transparent' : colors.White;
}, function (props) {
  return !props.transparentBackground && "\n    box-shadow: 0px 2px 5px 0px rgba(0, 0, 0, 0.05),\n      0px 2px 7px 0px rgba(0, 0, 0, 0.05)\n  ";
}, function (props) {
  return props.margin;
});
var Ellipsis = styled.div.withConfig({
  displayName: "Pagination__Ellipsis",
  componentId: "sc-zcpmm5-1"
})(["height:2.8rem;width:2.8rem;display:flex;align-items:flex-end;justify-content:center;padding:0.4rem 0;font-size:1.4rem;color:", ";font-family:", ";"], colors.GreyLight, function (props) {
  return getFont(props.theme, 'bold');
});
var PageButton = styled.button.withConfig({
  displayName: "Pagination__PageButton",
  componentId: "sc-zcpmm5-2"
})(["min-width:2.8rem;padding:0 0.6rem;background-color:", ";color:", ";font-family:", ";font-size:1.4rem;border:none;border-radius:3px;cursor:pointer;", ";&:focus{outline:none;}&.focus-visible:focus,&:focus-visible{border:2px solid black;}"], function (props) {
  return props.active ? colors.BlueSky : 'transparent';
}, function (props) {
  return props.active ? colors.White : colors.BlueSky;
}, function (props) {
  return getFont(props.theme, 'bold');
}, function (props) {
  return !props.active && "\n    &:hover {\n      color: ".concat(colors.BlueDawn, ";\n    }\n  ");
});
var Pagination = function Pagination(props) {
  if (props.numberPages < 2 || props.selectedPage < 1 || props.selectedPage > props.numberPages) return null;

  // First page always displayed
  var pagesArray = [1];
  Array(props.numberPages).fill().forEach(function (_, i) {
    // Already included in the array
    if (i === 0 || i === props.numberPages - 1) return;

    // Page before the current page
    if (i > 0 && i === props.selectedPage - 2) {
      if (props.selectedPage !== 3 && i < props.numberPages - 3) {
        pagesArray.push('...');
      }
      pagesArray.push(i + 1);
    } else if (i < 4 && props.selectedPage < 3) {
      // Extra pages when at the beginning of pagination
      pagesArray.push(i + 1);
      if (i === 3 && props.numberPages > 5) {
        pagesArray.push('...');
      }
    } else if (i > props.numberPages - 5 && props.selectedPage > props.numberPages - 3) {
      // Extra pages when at the end of pagination
      if (i === props.numberPages - 4 && props.numberPages > 5) {
        pagesArray.push('...');
      }
      pagesArray.push(i + 1);
    } else if (i < props.numberPages - 1 && i === props.selectedPage - 1) {
      // Current page
      pagesArray.push(i + 1);
    } else if (i < props.numberPages - 1 && i === props.selectedPage) {
      // Page after the current page
      pagesArray.push(i + 1);
      pagesArray.push('...');
    }
  });

  // Last page always displayed
  pagesArray.push(props.numberPages);
  var clickHandler = function clickHandler(page) {
    props.onClick(page);
  };
  return /*#__PURE__*/React.createElement(Wrapper, {
    transparentBackground: props.transparentBackground,
    margin: props.margin
  }, props.selectedPage !== 1 && /*#__PURE__*/React.createElement(componentsButton.IconButton, {
    icon: /*#__PURE__*/React.createElement(ArrowLeft, null),
    kind: "secondary",
    size: "sm",
    accessibilityText: props.prevAccessibilityLabel,
    onClick: function onClick() {
      return clickHandler(props.selectedPage - 1);
    }
  }), pagesArray.map(function (page, index) {
    return page === '...' ? /*#__PURE__*/React.createElement(Ellipsis, {
      key: "".concat(page, "-").concat(index)
    }, page) : /*#__PURE__*/React.createElement(PageButton, {
      key: page,
      active: page === props.selectedPage,
      onClick: function onClick() {
        return clickHandler(page);
      }
    }, page);
  }), props.selectedPage !== props.numberPages && /*#__PURE__*/React.createElement(componentsButton.IconButton, {
    icon: /*#__PURE__*/React.createElement(ArrowRight, null),
    kind: "secondary",
    size: "sm",
    accessibilityText: props.nextAccessibilityLabel,
    onClick: function onClick() {
      return clickHandler(props.selectedPage + 1);
    }
  }));
};
exports.Pagination = Pagination;